// Co-op scrolling: the room scrolls together. Whoever last scrolled by their own hand leads (that rule is
// planCoopSync's, packages/shared/src/domain/coop.ts); this side works out what my scroll is, tells my own
// scrolling (wheel, touch, keys, the scrollbar) apart from following, and eases this screen to the room's.
//
// A position travels per scroller: the page itself (AppShell's main.content; the window never scrolls)
// and every grid inside it that scrolls on this screen, whether or not anyone has moved it yet. A scroller
// is known by name (its data-coop-scroll, or its first class) and its place among those with that name.
// Where it's scrolled to is the element at the top-left corner of what shows of it, past a sticky header
// row or pinned column, and where the corner falls in it (a CoopAnchor), so people with different window
// sizes land on the same row; fractions of the scroll range are the fallback, and say the ends exactly.
//
// Layouts differ: a grid that scrolls by itself on one screen sits in a page that scrolls on another (a
// landscape phone, a short window). So the row a position names is brought to the top of whichever
// scroller around it really scrolls that way here: the grid, or the page.
import type { CoopAnchor, CoopScroll, CoopScrollPos } from '@technohull/shared';
import { anchorOf, isPageAnchor, resolveAnchor } from './coopAnchor.js';

/** what's on screen at a viewport point (document.elementFromPoint; tests stand in their own). */
export type HitTest = (x: number, y: number) => Element | null;
const hitTest: HitTest = (x, y) => (typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : null);

const NAME_RE = /^[A-Za-z][\w-]{0,39}$/;
const MAX_SCROLLERS = 4; // the page and the three grids that matter most
const MAX_INDEX = 99;
/** the grids with scrollbars of their own that pages have; any other scroller counts once it's scrolled. */
const GRIDS = '[data-coop-scroll], .osheet-wrap, .tbl-wrap, .vs-wrap, .mes-report-scroll';

/** the page's own scroller. */
export const pageScroller = (): HTMLElement | null => document.querySelector<HTMLElement>('main.content');

/** a field someone types into (not a box to tick, nor a button). Typing in one never counts as scrolling,
 *  and the room's scroll waits for whoever is editing one (see heldFor in createScrollShare). */
export function isEditableField(el: Element | null | EventTarget): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return !el.disabled;
  return el instanceof HTMLInputElement && !el.disabled && !el.readOnly
    && !/^(checkbox|radio|button|submit|reset|range|color|file|image|hidden)$/.test(el.type);
}

const TEXT_RE = /^(?:text|search|email|tel|url|password|number)$/;
/** a box text is typed into: not a dropdown or a date, which save the moment they're picked. */
const isTextBox = (el: Element): el is HTMLInputElement | HTMLTextAreaElement =>
  el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && TEXT_RE.test(el.type));

/** something typed into a box that isn't saved yet. A box that saves when you leave it keeps what it
 *  started with as its default; one the page keeps in step as you type (a search box) always matches. */
const hasDraft = (el: Element): boolean => isTextBox(el) && el.value !== el.defaultValue;

/** the field I'm editing on the page, if I'm in one. */
export function editingField(main: Element | null = pageScroller()): HTMLElement | null {
  const el = document.activeElement;
  return main && isEditableField(el) && main.contains(el) ? el : null;
}

const formControl = (el: Element) => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement;

export type Axis = 'x' | 'y';
/** how far a scroller scrolls along an axis on this screen; 0 when it doesn't (it fits, or it's clipped). */
export function rangeOf(el: Element, axis: Axis): number {
  const r = axis === 'y' ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth;
  if (r <= 1) return 0;
  const cs = getComputedStyle(el);
  return /auto|scroll/.test((axis === 'y' ? cs.overflowY : cs.overflowX) || cs.overflow) ? r : 0;
}

/** the nearest scroller around an element (up to the page itself) that scrolls along an axis here. */
export function scrollerAround(el: Element, axis: Axis, main: Element): HTMLElement | null {
  for (let n = el.parentElement; n; n = n.parentElement) {
    if (rangeOf(n, axis)) return n;
    if (n === main) break;
  }
  return null;
}

/** how the others find a scroller: `main` for the page itself, or an inner one's name and its place among
 *  those with that name on the page. Null for one that can't be told apart (no name, or not on the page). */
export function scrollerKey(el: Element, main: Element): { s: string; i: number } | null {
  if (el === main) return { s: 'main', i: 0 };
  if (!main.contains(el)) return null;
  const named = el.getAttribute('data-coop-scroll');
  const name = named && NAME_RE.test(named) ? named : el.classList[0];
  if (!name || name === 'main' || !NAME_RE.test(name)) return null;
  const all = main.querySelectorAll(name === named ? `[data-coop-scroll="${name}"]` : `.${name}`);
  const i = Array.prototype.indexOf.call(all, el) as number;
  return i >= 0 && i <= MAX_INDEX ? { s: name, i } : null;
}

/** the scroller a key names on this screen. */
export function findScroller(s: string, i: number, main: Element): HTMLElement | null {
  if (s === 'main') return main as HTMLElement;
  if (!NAME_RE.test(s)) return null;
  const named = main.querySelectorAll<HTMLElement>(`[data-coop-scroll="${s}"]`);
  return (named.length ? named : main.querySelectorAll<HTMLElement>(`.${s}`))[i] ?? null;
}

/** the outermost sticky (or fixed) element between `hit` and its scroller, and which way it sticks. */
export function stuckBetween(hit: Element, scroller: Element): { el: Element; v: boolean; h: boolean; fixed: boolean } | null {
  let out: { el: Element; v: boolean; h: boolean; fixed: boolean } | null = null;
  for (let n: Element | null = hit; n && n !== scroller; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (cs.position === 'sticky' || cs.position === 'fixed') {
      out = { el: n, v: !!cs.top && cs.top !== 'auto', h: !!cs.left && cs.left !== 'auto', fixed: cs.position === 'fixed' };
    }
  }
  return out;
}

export interface Box { l: number; t: number; r: number; b: number }
const clientBox = (el: Element): Box => {
  const r = el.getBoundingClientRect();
  const l = r.left + el.clientLeft;
  const t = r.top + el.clientTop;
  return { l, t, r: l + el.clientWidth, b: t + el.clientHeight };
};

/** the part of a scroller's inside that's on screen: its own box, cut down by the page's and the window. */
export function visibleBox(el: Element, main: Element): Box {
  const b = clientBox(el);
  const cut = (o: Box) => { b.l = Math.max(b.l, o.l); b.t = Math.max(b.t, o.t); b.r = Math.min(b.r, o.r); b.b = Math.min(b.b, o.b); };
  if (el !== main) cut(clientBox(main));
  cut({ l: 0, t: 0, r: window.innerWidth, b: window.innerHeight });
  return b;
}

/** the top-left corner of what shows of a scroller, past a sticky header row or pinned column, and the
 *  element there. Null when none of it shows, or something that isn't its content covers it (a popover). */
export function contentPoint(el: Element, main: Element, hit: HitTest = hitTest): { x: number; y: number; at: Element } | null {
  const v = visibleBox(el, main);
  const own = clientBox(el);
  let x = v.l + 1;
  let y = v.t + 1;
  let padded = false;
  for (let tries = 0; tries < 8 && x < v.r && y < v.b; tries++) {
    const h = hit(x, y);
    if (!h || (h !== el && !el.contains(h))) return null;
    if (h === el) {
      // the scroller's own padding (it scrolls away with the content), then a gap between its children
      if (!padded) {
        padded = true;
        const cs = getComputedStyle(el);
        x = Math.max(x, own.l + (parseFloat(cs.paddingLeft) || 0) - el.scrollLeft + 1);
        y = Math.max(y, own.t + (parseFloat(cs.paddingTop) || 0) - el.scrollTop + 1);
      } else {
        y += 12;
      }
      continue;
    }
    const st = stuckBetween(h, el);
    if (!st) return { x, y, at: h };
    if (st.fixed) return null;
    // past the sticky thing, and past whatever of it spills over its edge (a pinned cell's ✕ button)
    const sr = st.el.getBoundingClientRect();
    const hr = h.getBoundingClientRect();
    const nx = st.h ? Math.max(x, sr.right + 1, hr.right + 1) : x;
    const ny = st.v ? Math.max(y, sr.bottom + 1, hr.bottom + 1) : y;
    if (nx === x && ny === y) return { x, y, at: h };
    x = nx;
    y = ny;
  }
  return null;
}

/** what a position is anchored to: the element at the corner, or the outermost grid around it inside this
 *  scroller that scrolls up and down by itself (its rows move as it scrolls, it only moves with this one).
 *  A grid that only scrolls sideways keeps its rows, so another screen can line them up however it scrolls. */
function anchorTarget(at: Element, el: Element): Element {
  let target = at;
  for (let n: Element | null = at; n && n !== el; n = n.parentElement) if (rangeOf(n, 'y')) target = n;
  return target;
}

/** the anchor of what shows at a scroller's top-left corner, as the others can find it. */
function cornerAnchor(el: Element, main: Element, hit: HitTest): CoopAnchor | null {
  const pt = contentPoint(el, main, hit);
  const a = pt ? anchorOf(anchorTarget(pt.at, el), pt.x, pt.y) : null;
  return a && isPageAnchor(a) ? a : null;
}

/** how far along a scroll range (0..1), with the ends exact so they're kept whatever the window size. */
function along(pos: number, range: number): number {
  if (range < 1 || pos < 1) return 0;
  if (range - pos < 1) return 1;
  return Math.round((pos / range) * 10000) / 10000;
}

/** where a scroller is scrolled to, as the others can find it; null when it doesn't scroll here, or they
 *  couldn't find it. */
export function scrollPosOf(el: HTMLElement, main: Element, hit: HitTest = hitTest): CoopScrollPos | null {
  const rx = rangeOf(el, 'x');
  const ry = rangeOf(el, 'y');
  const key = rx || ry ? scrollerKey(el, main) : null;
  if (!key) return null;
  return {
    s: key.s,
    i: key.i,
    a: cornerAnchor(el, main, hit),
    fx: rx ? along(el.scrollLeft, rx) : null,
    fy: ry ? along(el.scrollTop, ry) : null,
  };
}

/** the scrollers inside the page worth sharing: those last scrolled first, then those scrolled away from
 *  their start, then the page's grids in order. */
export function scrollersOf(main: HTMLElement, moved: ReadonlyMap<Element, number> = new Map()): HTMLElement[] {
  const recent = [...moved].sort((a, b) => b[1] - a[1]).map(([el]) => el);
  const grids = [...main.querySelectorAll<HTMLElement>(GRIDS)];
  const away = grids.filter((el) => el.scrollTop > 0 || el.scrollLeft > 0);
  const out: HTMLElement[] = [];
  for (const el of [...recent, ...away, ...grids]) {
    if (el instanceof HTMLElement && el !== main && el.isConnected && main.contains(el) && !formControl(el) && !out.includes(el)) out.push(el);
  }
  return out;
}

/** the page's scroll: the page itself first (when it scrolls here), then the grids inside it that do. */
export function scrollOf(main: HTMLElement, inner: readonly HTMLElement[], hit: HitTest = hitTest): CoopScroll {
  const out: CoopScroll = [];
  for (const el of [main, ...inner]) {
    if (out.length >= MAX_SCROLLERS) break;
    const p = scrollPosOf(el, main, hit);
    if (p) out.push(p);
  }
  return out;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** the spot `a` names in `e`, brought to the corner of what shows of scroller `v` along an axis: v's
 *  scroll for it. Null when e doesn't move as v scrolls that way (a sticky header row, a pinned column). */
function spotScroll(v: HTMLElement, e: Element, a: CoopAnchor, axis: Axis, corner: { x: number; y: number }): number | null {
  const st = stuckBetween(e, v);
  if (axis === 'y' ? st?.v : st?.h) return null;
  const r = e.getBoundingClientRect();
  return axis === 'y' ? v.scrollTop + r.top + a.ay * r.height - corner.y : v.scrollLeft + r.left + a.ax * r.width - corner.x;
}

/** one way to scroll a scroller along an axis, and how much it counts (see scrollTargets). */
interface Aim { rank: number; to: number; atMost: boolean }
export type ScrollTargets = Map<HTMLElement, { top?: number; left?: number }>;

/** where to scroll the scrollers here to show what the room's scroll shows. The element at the corner of
 *  what showed in a scroller goes to the corner of whichever scroller around it scrolls that way here: the
 *  one named, a grid inside it, or the page around it. The fractions say the ends exactly, and stand in
 *  when that element isn't here (rows still loading). When two positions move one scroller, a grid's rows
 *  win over the page's (they're what's being read); a grid at its very start only says how far down the
 *  page can be, so the top of the page still shows where it did for them. */
export function scrollTargets(scroll: CoopScroll, main: HTMLElement, hit: HitTest = hitTest): ScrollTargets {
  const aims = new Map<HTMLElement, Record<Axis, Aim[]>>();
  const aim = (el: HTMLElement, axis: Axis, rank: number, to: number, atMost = false) => {
    let a = aims.get(el);
    if (!a) aims.set(el, (a = { x: [], y: [] }));
    a[axis].push({ rank, to, atMost });
  };
  const corners = new Map<HTMLElement, { x: number; y: number }>();
  const cornerOf = (v: HTMLElement) => {
    let c = corners.get(v);
    if (!c) {
      const b = visibleBox(v, main);
      c = contentPoint(v, main, hit) ?? { x: b.l + 1, y: b.t + 1 };
      corners.set(v, c);
    }
    return c;
  };
  for (const pos of scroll) {
    const s = findScroller(pos.s, pos.i, main);
    if (!s) continue;
    const found = pos.a ? resolveAnchor(pos.a) : null;
    const box = found && found !== s && s.contains(found) ? found.getBoundingClientRect() : null;
    const e = box && (box.width || box.height) ? found : null;
    for (const axis of ['y', 'x'] as const) {
      const f = axis === 'y' ? pos.fy : pos.fx;
      const v = e ? scrollerAround(e, axis, main) : null;
      const to = v && e && pos.a ? spotScroll(v, e, pos.a, axis, cornerOf(v)) : null;
      if (v && to != null) {
        // their scroller scrolled this way: its rows lead; one that didn't only says what showed at its corner
        if (v !== s) { if (f != null) aim(v, axis, 0, to, f === 0); }
        else if (f != null) aim(v, axis, 1, f === 0 ? 0 : f === 1 ? rangeOf(v, axis) : to);
        else aim(v, axis, 2, to);
        continue;
      }
      const range = rangeOf(s, axis);
      if (f != null && range) aim(s, axis, 1, f * range);
    }
  }
  const out: ScrollTargets = new Map();
  for (const [el, axes] of aims) {
    const t: { top?: number; left?: number } = {};
    for (const axis of ['y', 'x'] as const) {
      const list = axes[axis];
      if (!list.length) continue;
      // the best rank, and of equals the later position (a grid comes after the page)
      const best = (l: Aim[]) => l.reduce<Aim | null>((b, a) => (!b || a.rank <= b.rank ? a : b), null);
      const pick = best(list)!;
      let to = pick.to;
      if (pick.atMost) {
        const other = best(list.filter((a) => !a.atMost));
        to = Math.min(other ? other.to : axis === 'y' ? el.scrollTop : el.scrollLeft, to);
      }
      t[axis === 'y' ? 'top' : 'left'] = clamp(to, 0, rangeOf(el, axis));
    }
    out.set(el, t);
  }
  return out;
}

/** where to scroll `el` to on this screen to show what `pos` showed on theirs (one position on its own). */
export function scrollTarget(pos: CoopScrollPos, el: HTMLElement, main: HTMLElement, hit: HitTest = hitTest): { top?: number; left?: number } {
  return scrollTargets([pos], main, hit).get(el) ?? {};
}

/** The top-left of what shows of the scroller a pointer is over (the nearest around it that scrolls here),
 *  sent along with the pointer. While two people's views catch up with each other, the others draw the
 *  pointer where it is on its owner's screen, relative to this, rather than sliding with rows its owner
 *  hasn't scrolled to yet. Null off the page, or where nothing around it scrolls. */
export function frameOf(hit: Element, main: Element, hitFn: HitTest = hitTest): CoopAnchor | null {
  if (!main.contains(hit)) return null;
  for (let n: Element | null = hit; n; n = n.parentElement) {
    if (!formControl(n) && (rangeOf(n, 'y') || rangeOf(n, 'x'))) return cornerAnchor(n, main, hitFn);
    if (n === main) break;
  }
  return null;
}

// ── sharing it ─────────────────────────────────────────────────────────────────────────────────
const INPUT_MS = 350; // a scroll this soon after my own wheel, touch, scrolling key or press is mine
const CHAIN_MS = 160; // so is one carrying straight on from a scroll of mine (momentum, a smooth key scroll)
const HOLD_MS = 1500; // the room's scroll waits this long after my own input, then catches up
const RETARGET_MS = 300; // while gliding, the spot is looked up again this often (rows still arriving)
const GLIDE_MS = 380; // a single move eases in over about one fast poll, like the pointers
const KEEP_PACE_MS = 700; // the room's scroll moved again this soon: keep pace with it at an even speed
const WRITE_MS = 250; // a scroll event this soon after I set that very position is my following
const TYPING_MS = 4000; // a field holds the room's scroll this long after my last key in it (or my click into a box)

/** a key that scrolls the page, or moves the focus (which can scroll): never a letter typed into a field,
 *  whose caret the browser keeps in view by itself. */
function scrollingKey(e: KeyboardEvent): boolean {
  if (e.key === 'Tab') return true;
  if (isEditableField(e.target)) return false;
  if (/^(Arrow(Up|Down|Left|Right)|Page(Up|Down)|Home|End)$/.test(e.key)) return true;
  return e.key === ' ' && !(e.target instanceof Element && e.target.closest('button, a, summary, [role="button"], [role="switch"], [role="checkbox"], [role="tab"]'));
}

export interface ScrollShare {
  /** my scroll to send (undefined while scrolling together is off); `lead`: I scrolled it by hand since the
   *  room last heard from me, `age` ms ago; `own`: it's my own view, not the room's I'm showing. */
  mine(): { scroll: CoopScroll | undefined; lead: boolean; age: number; own: boolean };
  /** the room's scroll, in answer to the heartbeat that took mine(); null when it's mine already or nobody
   *  leads it. */
  follow(scroll: CoopScroll | null): void;
  /** a new page: the last page's scrollers and scroll are forgotten. */
  newPage(): void;
  /** scrolling together switched on or off. */
  setOn(on: boolean): void;
  /** whether my view is waiting for a field I'm editing (the room's scroll would move it away from me). */
  held(): boolean;
  /** whether it's waiting while the room's scroll has moved on (the banner says so). */
  paused(): boolean;
  stop(): void;
}

/** one way a scroller glides: from where it was to the room's spot, eased (a single move) or at an even
 *  speed (keeping pace with someone scrolling, one step per poll). */
interface Glide { from: number; to: number; t0: number; dur: number; even: boolean }
interface Ease { el: HTMLElement; top: Glide | null; left: Glide | null }

const glideAt = (g: Glide, t: number): number => {
  const k = g.dur > 0 ? Math.min(1, (t - g.t0) / g.dur) : 1;
  return g.from + (g.to - g.from) * (g.even ? k : 1 - (1 - k) ** 3);
};
/** a glide; a long way off it starts a screen short, rather than racing through every row on the way. */
function glide(from: number, to: number, screen: number, t0: number, dur: number, even: boolean): Glide {
  if (Math.abs(to - from) > screen * 2) from = to - Math.sign(to - from) * screen;
  return { from, to, t0, dur, even };
}

/** Listens for scrolling while co-op is on. `onOwnScroll` runs on every scroll of my own (the sync loop
 *  speeds up while it lasts). */
export function createScrollShare(opts: { on: boolean; onOwnScroll: () => void }): ScrollShare {
  let on = opts.on;
  const moved = new Map<HTMLElement, number>(); // scrollers moved on this page → when
  const written = new WeakMap<Element, { top: number; left: number; at: number }>();
  const eases = new Map<HTMLElement, Ease>();
  const aimedAt = new WeakMap<Element, number>(); // when the room's spot for a scroller last moved
  let lastInput = -Infinity;
  let lastOwn = -Infinity;
  let typed: { el: Element; at: number } | null = null; // the field I last typed into (or clicked into to type)
  let released: Element | null = null; // a field my own scrolling let go of: it holds nothing until I type in it again
  let behind = false; // a field held my view while the room's scroll moved on
  let rejoin = false; // and then my scrolling let go of it: it brings me back to the room, rather than leading it
  let pressed = false;
  let leading = false; // I scrolled by hand, and the room hasn't heard it yet
  let settled = false; // the room has answered since I got here (joined, switched on, a new page)
  let mine: CoopScroll | null = null;
  let source: 'own' | 'room' | 'page' = 'page'; // where `mine` comes from (page: what my page shows by itself)
  let room: CoopScroll | null = null; // the room's scroll I'm showing
  let askedAt = -Infinity; // when my last heartbeat went out (the room's answer to it can't know what I did since)
  let raf = 0;
  let retargetAt = 0;
  let waiting: number | undefined;

  const now = () => performance.now();
  const holding = () => pressed || now() - Math.max(lastInput, lastOwn) < HOLD_MS;
  const reduced = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  /** the field my view waits for: one I'm typing in (for a few seconds after my last key, or after clicking
   *  into a box), or one holding something typed that isn't saved yet, until I leave it. Focus merely resting
   *  in a field (a dropdown or a date that saved as it was picked, a box clicked and left) holds nothing. */
  const heldFor = (main: Element | null): HTMLElement | null => {
    const f = editingField(main);
    if (!f || f === released) return null;
    return (typed?.el === f && now() - typed.at < TYPING_MS) || hasDraft(f) ? f : null;
  };
  // the grid that field is in, and the page, are left where they are: the field stays in view
  const keepsStill = (el: Element, field: Element | null) => !!field && el.contains(field);

  const typing = (el: Element) => {
    typed = { el, at: now() };
    if (released === el) released = null;
  };
  const onInput = () => { lastInput = now(); };
  const onKey = (e: KeyboardEvent) => {
    if (scrollingKey(e)) lastInput = now();
    else if (isEditableField(e.target)) typing(e.target);
  };
  // clicking (or tabbing) into a box is the start of typing in it; a box the page focuses by itself isn't
  const onFocus = (e: FocusEvent) => {
    if (e.target instanceof Element && isTextBox(e.target) && (pressed || now() - lastInput < INPUT_MS)) typing(e.target);
  };
  const onDown = () => { pressed = true; lastInput = now(); };
  const onUp = () => { pressed = false; lastInput = now(); };
  // a release that never reached the page (let go outside the window) mustn't hold the room's scroll off
  const onMove = (e: PointerEvent) => { if (pressed && !e.buttons) pressed = false; };
  const onScroll = (e: Event) => {
    const el = e.target;
    if (!(el instanceof HTMLElement) || formControl(el)) return;
    const main = pageScroller();
    if (!main || (el !== main && !main.contains(el))) return;
    const t = now();
    moved.set(el, t);
    const w = written.get(el);
    if (w && t - w.at < WRITE_MS && Math.abs(el.scrollTop - w.top) < 2 && Math.abs(el.scrollLeft - w.left) < 2) return; // following
    if (!pressed && t - lastInput > INPUT_MS && t - lastOwn > CHAIN_MS) return; // the page's doing, not mine
    lastOwn = t;
    eases.clear(); // my hand wins at once: no easing
    // my hand moved my view, so the field I was editing holds it no longer. If the room's scroll moved on
    // meanwhile, this scrolling brings me back to it once my hand rests, rather than leading the room back
    // to my old rows.
    const field = heldFor(main);
    if (field) {
      released = field;
      if (on && behind) rejoin = true;
    }
    window.clearTimeout(waiting);
    if (on && rejoin) {
      waiting = window.setTimeout(catchUp, HOLD_MS);
    } else {
      if (on) {
        // scrolling on my own (scroll together off) leads nothing, then or later
        leading = true;
        source = 'own';
      }
      room = null; // no catching up later with a room scroll from before this
    }
    opts.onOwnScroll();
  };
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  window.addEventListener('wheel', onInput, { capture: true, passive: true });
  window.addEventListener('touchstart', onInput, { capture: true, passive: true });
  window.addEventListener('touchmove', onInput, { capture: true, passive: true });
  window.addEventListener('keydown', onKey, { capture: true });
  window.addEventListener('focusin', onFocus, { capture: true });
  window.addEventListener('pointerdown', onDown, { capture: true, passive: true });
  window.addEventListener('pointerup', onUp, { capture: true, passive: true });
  window.addEventListener('pointercancel', onUp, { capture: true, passive: true });
  window.addEventListener('pointermove', onMove, { capture: true, passive: true });
  window.addEventListener('blur', onUp);

  const write = (el: HTMLElement, top: number, left: number) => {
    el.scrollTo({ top, left, behavior: 'instant' });
    written.set(el, { top: el.scrollTop, left: el.scrollLeft, at: now() });
  };
  const frame = () => {
    raf = 0;
    const t = now();
    if (!on || holding()) { eases.clear(); return; }
    const main = pageScroller();
    if (room && main && t - retargetAt > RETARGET_MS) {
      retargetAt = t;
      for (const [el, to] of scrollTargets(room, main)) {
        const e = eases.get(el);
        if (e?.top && to.top != null) e.top.to = to.top;
        if (e?.left && to.left != null) e.left.to = to.left;
      }
    }
    const field = heldFor(main);
    for (const e of [...eases.values()]) {
      if (!e.el.isConnected || keepsStill(e.el, field)) { eases.delete(e.el); continue; }
      write(e.el, e.top ? glideAt(e.top, t) : e.el.scrollTop, e.left ? glideAt(e.left, t) : e.el.scrollLeft);
      if ((!e.top || t - e.top.t0 >= e.top.dur) && (!e.left || t - e.left.t0 >= e.left.dur)) eases.delete(e.el);
    }
    if (eases.size) raf = requestAnimationFrame(frame);
  };

  /** glide every scroller the room's scroll moves to where it shows it. */
  const show = (scroll: CoopScroll) => {
    const main = pageScroller();
    if (!main) return;
    const t = now();
    const jump = reduced();
    const field = heldFor(main);
    let kept = false;
    for (const [el, to] of scrollTargets(scroll, main)) {
      if (keepsStill(el, field)) {
        eases.delete(el);
        if ((to.top != null && Math.abs(to.top - el.scrollTop) >= 2) || (to.left != null && Math.abs(to.left - el.scrollLeft) >= 2)) kept = true;
        continue;
      }
      const e = eases.get(el);
      const atTop = e?.top ? glideAt(e.top, t) : el.scrollTop;
      const atLeft = e?.left ? glideAt(e.left, t) : el.scrollLeft;
      const moveTop = to.top != null && Math.abs(to.top - (e?.top ? e.top.to : atTop)) >= 1;
      const moveLeft = to.left != null && Math.abs(to.left - (e?.left ? e.left.to : atLeft)) >= 1;
      if (!moveTop && !moveLeft) continue; // already there, or on the way
      // the spot moved again soon after the last time: someone is scrolling, so keep pace with them at an
      // even speed, one step per poll; otherwise a single move eases in
      const last = aimedAt.get(el) ?? -Infinity;
      const even = t - last < KEEP_PACE_MS;
      const dur = jump ? 0 : even ? clamp(t - last, 150, 600) : GLIDE_MS;
      aimedAt.set(el, t);
      eases.set(el, {
        el,
        top: moveTop ? glide(atTop, to.top!, el.clientHeight, t, dur, even) : e?.top ?? null,
        left: moveLeft ? glide(atLeft, to.left!, el.clientWidth, t, dur, even) : e?.left ?? null,
      });
    }
    // the room's scroll would have moved what the field keeps still: I'm behind it while I edit
    behind = kept;
    if (!kept) rejoin = false;
    retargetAt = t;
    if (eases.size && !raf) raf = requestAnimationFrame(frame);
  };

  const catchUp = () => {
    window.clearTimeout(waiting);
    waiting = undefined;
    if (!on || !room) return;
    // still busy with my own scrolling (or a scrolling key, or pressing): look again once that has settled
    if (holding()) { waiting = window.setTimeout(catchUp, 250); return; }
    mine = room;
    source = 'room';
    show(room);
  };

  return {
    mine() {
      askedAt = now();
      if (!on) return { scroll: undefined, lead: false, age: 0, own: false };
      // my own view is worked out afresh each time (rows arriving, a grid showing up); the room's I'm
      // showing goes back as it came, so the room knows I'm with it
      if (source !== 'room' || !mine) {
        const main = pageScroller();
        if (main) mine = scrollOf(main, scrollersOf(main, moved));
      }
      return { scroll: mine ?? undefined, lead: leading, age: leading ? Math.round(askedAt - lastOwn) : 0, own: settled && source !== 'room' };
    },
    follow(scroll) {
      // an answer to a heartbeat sent before I last scrolled myself would drag me back to where I was
      if (!on || askedAt < lastOwn) return;
      leading = false; // the room has my scroll
      settled = true;
      if (scroll) room = scroll;
      else if (source !== 'room') return; // I lead it (or nobody does): nothing to follow
      // a null while I'm showing the room's scroll still lines me up with it (rows arriving late, a field
      // I was typing in just left)
      catchUp();
    },
    newPage() {
      moved.clear();
      eases.clear();
      mine = null;
      source = 'page';
      room = null;
      leading = false;
      settled = false;
      behind = false;
      rejoin = false;
      window.clearTimeout(waiting);
    },
    setOn(next) {
      on = next;
      eases.clear();
      room = null;
      // switched back on: what I see now goes out as mine, but leads nothing until the room has answered
      // (it then brings me to its scroll)
      mine = null;
      source = 'page';
      leading = false;
      settled = false;
      behind = false;
      rejoin = false;
    },
    held() {
      return on && !!heldFor(pageScroller());
    },
    paused() {
      return on && behind && !!heldFor(pageScroller());
    },
    stop() {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('wheel', onInput, { capture: true });
      window.removeEventListener('touchstart', onInput, { capture: true });
      window.removeEventListener('touchmove', onInput, { capture: true });
      window.removeEventListener('keydown', onKey, { capture: true });
      window.removeEventListener('focusin', onFocus, { capture: true });
      window.removeEventListener('pointerdown', onDown, { capture: true });
      window.removeEventListener('pointerup', onUp, { capture: true });
      window.removeEventListener('pointercancel', onUp, { capture: true });
      window.removeEventListener('pointermove', onMove, { capture: true });
      window.removeEventListener('blur', onUp);
      cancelAnimationFrame(raf);
      window.clearTimeout(waiting);
      eases.clear();
    },
  };
}
